import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, Copy, Archive, Star, ArrowLeft } from "lucide-react";
import API, { formatApiError } from "@/lib/api";

const inputCls = "mt-1.5 w-full rounded-xl border border-[#4b3a2f]/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#c9a24b]";

const BLOCK_TYPES = [
  { id: "text", label: "Texto (título, párrafo, imagen y botón)" },
  { id: "cards", label: "Tarjetas" },
  { id: "banner", label: "Banner / aviso" },
  { id: "faq", label: "Preguntas frecuentes" },
  { id: "video", label: "Video (YouTube/Vimeo)" },
  { id: "contact", label: "Datos de contacto" },
];

const EMPTY_BLOCK = {
  text: { type: "text", title: "", subtitle: "", body: "", image_url: "", button_text: "", button_link: "" },
  cards: { type: "cards", title: "", items: [] },
  banner: { type: "banner", text: "", button_text: "", button_link: "" },
  faq: { type: "faq", items: [] },
  video: { type: "video", title: "", video_url: "" },
  contact: { type: "contact", title: "Contacto" },
};

const BlockEditor = ({ block, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const b = block;
  const set = (k, v) => onChange({ ...b, [k]: v });
  return (
    <div className="rounded-2xl border border-[#e8ded4] bg-[#faf7f2] p-5" data-testid={`page-block-${b.type}`}>
      <div className="mb-3 flex items-center justify-between">
        <p className="font-mono-d text-[10px] uppercase tracking-widest text-[#a47b52]">
          {BLOCK_TYPES.find((t) => t.id === b.type)?.label}
        </p>
        <div className="flex gap-1">
          <button type="button" onClick={onMoveUp} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52]"><ArrowUp size={12} /></button>
          <button type="button" onClick={onMoveDown} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52]"><ArrowDown size={12} /></button>
          <button type="button" onClick={onDelete} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] hover:bg-[#c85a48] hover:text-white"><Trash2 size={12} /></button>
        </div>
      </div>

      {(b.type === "text" || b.type === "video" || b.type === "contact") && (
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Título</span>
          <input value={b.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} /></label>
      )}
      {b.type === "text" && (
        <>
          <label className="mt-3 block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Subtítulo</span>
            <input value={b.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} /></label>
          <label className="mt-3 block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Párrafo (un renglón por párrafo)</span>
            <textarea rows={5} value={b.body || ""} onChange={(e) => set("body", e.target.value)} className={inputCls} /></label>
          <label className="mt-3 block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Imagen (URL o subida)</span>
            <input value={b.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="https://… o /api/media/…" className={inputCls} /></label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Botón — texto</span>
              <input value={b.button_text || ""} onChange={(e) => set("button_text", e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Botón — enlace</span>
              <input value={b.button_link || ""} onChange={(e) => set("button_link", e.target.value)} className={inputCls} /></label>
          </div>
        </>
      )}
      {b.type === "cards" && (
        <>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Título de la sección</span>
            <input value={b.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} /></label>
          <div className="mt-3 space-y-3">
            {(b.items || []).map((card, i) => (
              <div key={i} className="grid gap-2 rounded-xl border border-[#e8ded4] bg-white p-3 sm:grid-cols-2">
                {["title", "text", "image_url", "link"].map((k) => (
                  <input key={k} value={card[k] || ""} placeholder={{ title: "Título", text: "Texto", image_url: "URL de imagen", link: "Enlace" }[k]} onChange={(e) => { const n = [...b.items]; n[i] = { ...card, [k]: e.target.value }; set("items", n); }} className={inputCls} />
                ))}
                <button type="button" onClick={() => set("items", b.items.filter((_, j) => j !== i))} className="text-left text-xs text-[#c85a48] hover:underline sm:col-span-2">Eliminar tarjeta</button>
              </div>
            ))}
            <button type="button" onClick={() => set("items", [...(b.items || []), { title: "", text: "", image_url: "", link: "" }])} className="inline-flex items-center gap-1.5 rounded-full bg-[#f2e9dd] px-4 py-2 text-xs font-semibold text-[#6b4e3d]"><Plus size={13} /> Agregar tarjeta</button>
          </div>
        </>
      )}
      {b.type === "banner" && (
        <>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Texto del aviso</span>
            <textarea rows={2} value={b.text || ""} onChange={(e) => set("text", e.target.value)} className={inputCls} /></label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Botón — texto</span>
              <input value={b.button_text || ""} onChange={(e) => set("button_text", e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Botón — enlace</span>
              <input value={b.button_link || ""} onChange={(e) => set("button_link", e.target.value)} className={inputCls} /></label>
          </div>
        </>
      )}
      {b.type === "faq" && (
        <div className="space-y-3">
          {(b.items || []).map((item, i) => (
            <div key={i} className="rounded-xl border border-[#e8ded4] bg-white p-3">
              <input value={item.q || ""} placeholder="Pregunta" onChange={(e) => { const n = [...b.items]; n[i] = { ...item, q: e.target.value }; set("items", n); }} className={inputCls} />
              <textarea rows={2} value={item.a || ""} placeholder="Respuesta" onChange={(e) => { const n = [...b.items]; n[i] = { ...item, a: e.target.value }; set("items", n); }} className={inputCls} />
              <button type="button" onClick={() => set("items", b.items.filter((_, j) => j !== i))} className="mt-1 text-xs text-[#c85a48] hover:underline">Eliminar</button>
            </div>
          ))}
          <button type="button" onClick={() => set("items", [...(b.items || []), { q: "", a: "" }])} className="inline-flex items-center gap-1.5 rounded-full bg-[#f2e9dd] px-4 py-2 text-xs font-semibold text-[#6b4e3d]"><Plus size={13} /> Agregar pregunta</button>
        </div>
      )}
      {b.type === "video" && (
        <label className="mt-3 block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">URL del video (embed)</span>
          <input value={b.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://www.youtube.com/embed/…" className={inputCls} /></label>
      )}
    </div>
  );
};

const PagesEditor = () => {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // página en edición (objeto)
  const [saving, setSaving] = useState(false);

  const load = () => API.get("/admin/pages").then((r) => setPages(r.data.pages)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ title: "Página nueva", slug: "", blocks: [], status: "published", order: pages.length + 1, is_home: false, visibility: "public", roles: [] });
  const openEdit = (p) => setEditing({ ...p });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await API.put(`/admin/pages/${editing.id}`, editing);
        toast.success("Página guardada");
      } else {
        await API.post("/admin/pages", editing);
        toast.success("Página creada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const action = (fn, msg) => async (p) => {
    try {
      await fn(p);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = (p) => {
    if (!window.confirm(`¿Eliminar la página "${p.title}"? Esta acción no se puede deshacer.`)) return;
    action((x) => API.delete(`/admin/pages/${x.id}`), "Página eliminada")(p);
  };

  const move = async (p, dir) => {
    const sorted = [...pages];
    const i = sorted.findIndex((x) => x.id === p.id);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
    try {
      await API.post("/admin/pages/reorder", { ids: sorted.map((x) => x.id) });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <div data-testid="pages-editor-form">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-sm text-[#6b5b52] hover:text-[#c9a24b]">
          <ArrowLeft size={15} /> Volver a la lista
        </button>
        <div className="mt-5 grid gap-4 rounded-2xl border border-[#e8ded4] bg-white p-6 sm:grid-cols-2">
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Nombre de la página *</span>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="page-form-title" className={inputCls} /></label>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">URL (/p/…)</span>
            <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="mi-pagina" data-testid="page-form-slug" className={inputCls} /></label>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Visibilidad</span>
            <select value={editing.visibility} onChange={(e) => setEditing({ ...editing, visibility: e.target.value })} data-testid="page-form-visibility" className={inputCls}>
              <option value="public">Pública</option>
              <option value="auth">Requiere iniciar sesión</option>
              <option value="role">Solo por rol</option>
            </select></label>
          {editing.visibility === "role" && (
            <div><span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">Roles con acceso</span>
              <div className="mt-2 flex gap-5 text-sm">
                {["admin", "customer"].map((r) => (
                  <label key={r} className="flex items-center gap-2">
                    <input type="checkbox" checked={editing.roles?.includes(r)} onChange={(e) => setEditing({ ...editing, roles: e.target.checked ? [...(editing.roles || []), r] : (editing.roles || []).filter((x) => x !== r) })} className="h-4 w-4 accent-[#c9a24b]" />
                    {r === "admin" ? "Administrador" : "Clientes"}
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-[#4b3a2f]">
            <input type="checkbox" checked={editing.status === "published"} onChange={(e) => setEditing({ ...editing, status: e.target.checked ? "published" : "archived" })} className="h-4 w-4 accent-[#c9a24b]" />
            Visible en el sitio
          </label>
        </div>

        <div className="mt-6 space-y-4">
          {(editing.blocks || []).map((b, i) => (
            <BlockEditor
              key={i}
              block={b}
              onChange={(nb) => { const n = [...editing.blocks]; n[i] = nb; setEditing({ ...editing, blocks: n }); }}
              onDelete={() => setEditing({ ...editing, blocks: editing.blocks.filter((_, j) => j !== i) })}
              onMoveUp={() => { if (i === 0) return; const n = [...editing.blocks]; [n[i], n[i - 1]] = [n[i - 1], n[i]]; setEditing({ ...editing, blocks: n }); }}
              onMoveDown={() => { const n = [...editing.blocks]; if (i >= n.length - 1) return; [n[i], n[i + 1]] = [n[i + 1], n[i]]; setEditing({ ...editing, blocks: n }); }}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <select
            onChange={(e) => { if (e.target.value) { setEditing({ ...editing, blocks: [...(editing.blocks || []), JSON.parse(JSON.stringify(EMPTY_BLOCK[e.target.value]))] }); } e.target.value = ""; }}
            value=""
            data-testid="page-add-block-select"
            className="rounded-full border border-[#4b3a2f]/20 bg-white px-5 py-3 text-sm text-[#6b5b52]"
          >
            <option value="">+ Agregar bloque…</option>
            {BLOCK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <button
            onClick={save}
            disabled={saving}
            data-testid="page-form-save-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#c9a24b] px-8 py-3 text-sm font-semibold text-[#4b3a2f] hover:bg-[#b8913b]"
          >
            Guardar página
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pages-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-[#6b5b52]">Crea páginas nuevas que aparecen automáticamente en el menú. Arrastra el orden con las flechas.</p>
        <button onClick={openNew} data-testid="pages-new-button" className="inline-flex items-center gap-2 rounded-full bg-[#c9a24b] px-6 py-3 text-sm font-semibold text-[#4b3a2f] hover:bg-[#b8913b]">
          <Plus size={16} /> Nueva página
        </button>
      </div>

      {pages.length === 0 ? (
        <p className="mt-8 rounded-2xl border border-dashed border-[#a47b52]/40 p-10 text-center text-sm text-[#6b5b52]">
          Aún no hay páginas personalizadas. La portada actual es la página de inicio por defecto.
        </p>
      ) : (
        <div className="mt-6 space-y-3">
          {pages.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#e8ded4] bg-white p-5" data-testid={`page-row-${p.slug}`}>
              <div>
                <p className="font-medium text-[#4b3a2f]">
                  {p.title}
                  {p.is_home && <span className="ml-2 rounded-full bg-[#c9a24b]/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#a47b52]">Inicio</span>}
                  {p.status === "archived" && <span className="ml-2 rounded-full bg-[#c85a48]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#c85a48]">Archivada</span>}
                </p>
                <p className="mt-0.5 font-mono-d text-xs text-[#a47b52]">/p/{p.slug} · {p.visibility === "public" ? "Pública" : p.visibility === "auth" ? "Requiere sesión" : `Rol: ${(p.roles || []).join(", ")}`}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => move(p, -1)} title="Subir" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#f2e9dd]"><ArrowUp size={14} /></button>
                <button onClick={() => move(p, 1)} title="Bajar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#f2e9dd]"><ArrowDown size={14} /></button>
                <button onClick={() => openEdit(p)} data-testid={`page-edit-${p.slug}`} title="Editar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#c9a24b] hover:text-white"><Pencil size={14} /></button>
                <button onClick={() => action((x) => API.post(`/admin/pages/${x.id}/duplicate`), "Página duplicada")(p)} data-testid={`page-duplicate-${p.slug}`} title="Duplicar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#c9a24b] hover:text-white"><Copy size={14} /></button>
                <button onClick={() => action((x) => API.post(`/admin/pages/${x.id}/set-home`), "Es la nueva página de inicio")(p)} data-testid={`page-sethome-${p.slug}`} title="Definir como inicio" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#c9a24b] hover:text-white"><Star size={14} /></button>
                <button onClick={() => action((x) => API.post(`/admin/pages/${x.id}/archive`), "Estado actualizado")(p)} data-testid={`page-archive-${p.slug}`} title={p.status === "published" ? "Archivar" : "Publicar"} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] hover:bg-[#c9a24b] hover:text-white"><Archive size={14} /></button>
                <button onClick={() => del(p)} data-testid={`page-delete-${p.slug}`} title="Eliminar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] hover:bg-[#c85a48] hover:text-white"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PagesEditor;
